import { isFamilyLoaded } from '../../../utils/fonts';
import {
  DEFAULT_EDITOR_FONT_FAMILY,
  DEFAULT_EDITOR_FONT_URL
} from '../components/contants';
import { MAX_WIDTH_SVG } from '../config/constants';
import {
  calculateFontSize,
  checkFontSupportsStyle,
  getFirstWrappedLine,
  importFont,
  waitForGoogleFonts,
  wrapText
} from './helpers';

export type OverlayData = {
  position: number;
  image: string;
  overlay: {
    url: string;
    originalUrl: string;
    uuid: string;
    align: string;
    primaryColor: string;
    secondaryColor: string;
    originalHeight: number;
    originalWidth: number;
  };
  text: Array<{
    text: string;
    x: number;
    y: number;
    width: number;
    height: number;

    textMetrics: any;
    // plainTextMetrics: any;
    boxWidth: number;
    boxHeight: number;
    color: string;
    textAlign: string;
    fontStyle: string;
    fontWeight: string;
    baseFontFamily: string;
    fontSize: number;
    lineHeight: number;
    flipX: boolean;
    flipY: boolean;
  }>;
  logo: Array<{
    url: string;
    x: number;
    y: number;
    width: number;
    height: number;
    type: string;
    bgColor: string;
  }>;
};

export type BrandData = {
  logoBackgroundColor: string;
  primaryColor: string;
  secondaryColor: string;
  logo: {
    url: string;
    type: 'circle' | 'square' | 'rectangle';
  }[];
  fonts: {
    fontFamily: string;
    fontFaceUrl: string;
    normal: string;
    bold: string;
  };
};

export interface LineMetrics {
  width: number;
  ascent: number;
  descent: number;
  height: number;
  alphabeticBaseline: number;
}

export const calculateAlignmentX = (
  align: any,
  baseWidth: number,
  overlayWidth: number
): number => {
  if (align.includes('right')) return Math.round(baseWidth - overlayWidth);
  if (align.includes('center'))
    return Math.round((baseWidth - overlayWidth) / 2);
  return 0; // Default to left alignment
};

export const calculateAlignmentY = (
  align: any,
  baseHeight: number,
  overlayHeight: number
): number => {
  if (align.includes('bottom')) return Math.round(baseHeight - overlayHeight);
  if (align.includes('center'))
    return Math.round((baseHeight - overlayHeight) / 2);
  return 0; // Default to top alignment
};

const rgbaToHex = ({
  r,
  g,
  b,
  a
}: {
  r: number;
  g: number;
  b: number;
  a: number;
}): string => {
  const toHex = (c: number) => c.toString(16).padStart(2, '0');
  const alpha = Math.round(a * 255);
  return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(alpha)}`;
};

const hsvaToHex = ({
  h,
  s,
  v,
  a
}: {
  h: number;
  s: number;
  v: number;
  a: number;
}): string => {
  s /= 100;
  v /= 100;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let [r, g, b] = [0, 0, 0];

  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  return rgbaToHex({
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
    a
  });
};

const toHex = (color: string): string | null => {
  const ctx = document.createElement('canvas').getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = color;
  const computed = ctx.fillStyle; // This converts named colors and other formats to a standard format

  // If the color is already in hex, return it
  if (computed.startsWith('#')) return computed;

  // Handle `rgb()` and `rgba()`
  const matchRGB = computed.match(
    /^rgb(a?)\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/
  );
  if (matchRGB) {
    const r = parseInt(matchRGB[2]);
    const g = parseInt(matchRGB[3]);
    const b = parseInt(matchRGB[4]);
    return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
  }

  return null;
};

// Function to replace SVG colors
const replaceSvgColors = async (
  svgContent: string,
  colorMappings: { originalColor: string; replacementColor: string }[]
): Promise<string> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgContent, 'image/svg+xml');
  const elements = doc.getElementsByTagName('*');

  const isTransparent = (color: string) => {
    return (
      !color ||
      color.toLowerCase() === 'none' ||
      color.toLowerCase() === 'transparent' ||
      /^rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/i.test(color) // rgba(0,0,0,0)
    );
  };

  for (let i = 0; i < elements.length; i++) {
    const el = elements[i];

    ['fill', 'stroke', 'stop-color'].forEach((attr) => {
      let color = el.getAttribute(attr);

      if (!color && attr === 'fill') {
        el.setAttribute('fill', 'currentColor');
        color = 'currentColor';
      }

      if (color && !isTransparent(color)) {
        const hexColor = toHex(color);
        if (hexColor) {
          const mapping = colorMappings.find(
            (map) => map.originalColor.toLowerCase() === hexColor.toLowerCase()
          );
          if (mapping) el.setAttribute(attr, mapping.replacementColor);
        }
      }
    });

    const style = el.getAttribute('style');
    if (style) {
      const updatedStyle = style.replace(
        /(fill|stroke|stop-color):\s*([^;]+)/gi,
        (match, property, color) => {
          if (isTransparent(color)) return match; // Skip transparent colors
          const hexColor = toHex(color);
          if (!hexColor) return match;
          const mapping = colorMappings.find(
            (map) => map.originalColor.toLowerCase() === hexColor.toLowerCase()
          );
          return mapping ? `${property}: ${mapping.replacementColor}` : match;
        }
      );
      el.setAttribute('style', updatedStyle);
    }
  }

  const serializer = new XMLSerializer();
  return serializer.serializeToString(doc);
};

export const convertSvgToPng = async (
  svgUrl: string,
  imageWidth: number,
  colorMappings: { originalColor: string; replacementColor: string }[],
  shouldReplaceColors: boolean = true
): Promise<Blob> => {
  try {
    const response = await fetch(svgUrl);
    let svgContent = await response.text();
    console.log('✅ SVG file fetched successfully');

    if (shouldReplaceColors) {
      svgContent = await replaceSvgColors(svgContent, colorMappings);
      // console.log('🎨 Colors replaced in SVG');
    } else {
      // console.log('🎨 Skipped color replacement');
    }

    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
    const svgUrlBlob = URL.createObjectURL(svgBlob);

    const img = await loadImage(svgUrlBlob);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('🚨 Canvas context not available');

    const aspectRatio = img.width / img.height;
    canvas.width = imageWidth;
    canvas.height = Math.round(imageWidth / aspectRatio);

    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (!blob) throw new Error('🚨 Failed to create PNG blob');
        resolve(blob);
      }, 'image/png');
    });
  } catch (error) {
    console.error('❌ Error in convertSvgToPng:', error);
    throw error;
  }
};

export const loadFont = async (
  fontName: string,
  fontUrl: string
): Promise<void> => {
  const fontFace = new FontFace(fontName, `url(${fontUrl})`);
  await fontFace.load();
  document.fonts.add(fontFace);
};
export async function getLineMetrics(
  fontSize: number,
  lines: string[],
  fontFamily: string,
  fontWeight: string,
  fontStyle: string
): Promise<LineMetrics[]> {
  // Wait for the family before measuring in it. `document.fonts.check()` is
  // not the test it reads as — it answers "can this be painted", so an unknown
  // family comes back true and the wait was always skipped.
  if (!isFamilyLoaded(fontFamily)) {
    await document.fonts.ready;
  }

  // console.log(`Using font: ${fontString}`);

  return lines.map((line) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = 'black';

    const metric = ctx.measureText(line);

    // console.log('metrics', fontFamily, metric);

    return {
      width: metric.width,
      ascent: metric.actualBoundingBoxAscent,
      descent: metric.actualBoundingBoxDescent,
      fontAscent: metric.fontBoundingBoxAscent,
      fontDescent: metric.fontBoundingBoxDescent,
      height: metric.actualBoundingBoxAscent + metric.actualBoundingBoxDescent,
      // @ts-ignore
      alphabeticBaseline: metric.alphabeticBaseline
    };
  });
}

export async function getTextMetrics(
  fontSize: number,
  line: string,
  fontFamily: string,
  fontWeight: string = 'normal',
  fontStyle: string = 'normal'
): Promise<TextMetrics> {
  const fontString = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}"`;

  // Wait for the specific font to load. See getLineMetrics above for why the
  // check is on the font set rather than on document.fonts.check().
  if (!isFamilyLoaded(fontFamily)) {
    await document.fonts.load(fontString);
  }

  // Create canvas and measure text
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context could not be created');

  ctx.font = fontString;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'black';

  const metrics = ctx.measureText(line);

  return metrics;
}

// const adjustFontSizes = async (
//   ctx: CanvasRenderingContext2D,
//   text: string,
//   fontName: string,
//   fontUrl: string,
//   lineHeight: number,
//   maxWidth: number,
//   maxHeight: number,
//   fontStyle: string,
//   fontWeight: string
// ): Promise<number> => {
//   try {
//     await importFont(fontUrl);
//   } catch (e) {
//     console.log('error', e);
//   }

//   await waitForGoogleFonts(fontUrl);

//   let fontSize = 1;
//   ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px '${fontName}'`;

//   const lines = text.split('\n');
//   const maxIterations = 500;
//   let iterations = 0;

//   while (true) {
//     let totalHeight = 0;
//     let maxLineWidth = 0;

//     for (const line of lines) {
//       const metrics = ctx.measureText(line);
//       totalHeight += fontSize * lineHeight; // Corrected line height calculation
//       maxLineWidth = Math.max(maxLineWidth, metrics.width);
//     }

//     if (totalHeight > maxHeight || maxLineWidth > maxWidth) {
//       break;
//     }

//     fontSize++;
//     ctx.font = `${fontSize}px '${fontName}'`;

//     iterations++;
//     if (iterations >= maxIterations) {
//       console.warn('Maximum iterations reached while adjusting font size.');
//       break;
//     }
//   }

//   fontSize = Math.max(1, fontSize - 1);
//   ctx.font = `${fontSize}px '${fontName}'`;
//   return fontSize;
// };

const adjustFontSizes = async (
  x: number,
  y: number,
  ctx: CanvasRenderingContext2D,
  text: string,
  fontName: string,
  fontUrl: string,
  baseFontSize: number,
  lineHeightMultiplier: number,
  maxWidth: number,
  maxHeight: number,
  fontStyle: string,
  fontWeight: string,
  baseFontMetrics: LineMetrics[],
  scaleToMetrics: LineMetrics[] | null = null
): Promise<{ calcFontSize: number; chosenIndex: number }> => {
  try {
    await importFont(fontUrl);
  } catch (e) {
    console.error('Error loading font:', e);
  }

  await waitForGoogleFonts(fontUrl);
  ctx.textBaseline = 'top';
  const lines = text.split('\n');

  //scaleXArray is a ratio array of the baseFont to newFontArray's widths
  //scaleYArray is a ratio array of the baseFont to newFontArray's heights
  const scaleXArray = scaleToMetrics
    .map((metric, index) => baseFontMetrics[index].width / metric.width)
    .filter(Number.isFinite);
  const scaleYArray = scaleToMetrics
    .map((metric, index) => baseFontMetrics[index].height / metric.height)
    .filter(Number.isFinite);

  // console.log('scaleXArray', scaleXArray, baseFontMetrics, scaleToMetrics);

  // We want to pick the minimum ratio here, hereby fitting into all the bounding boxes of the basefont text height and width wise
  const scaleX = Math.min(...scaleXArray);
  const scaleY = Math.min(...scaleYArray);
  const scale = Math.min(scaleX, scaleY);

  // Find the index of the chosen scale
  const scaleXIndex = scaleXArray.indexOf(scaleX);
  const scaleYIndex = scaleYArray.indexOf(scaleY);

  const chosenIndex = scale === scaleX ? scaleXIndex : scaleYIndex;

  // Calculate original font height using font metrics

  //This is the new font size for the new font the fulfills all the criteria above
  const adjustedFontSize = Math.floor(baseFontSize * scale);
  console.log('scales', scale, scaleX, scaleY, adjustedFontSize);
  // Apply the calculated font size
  ctx.font = `${fontStyle} ${fontWeight} ${adjustedFontSize}px '${fontName}'`;

  ctx.strokeStyle = 'blue';

  ctx.font = `${adjustedFontSize}px ${fontName}`;

  const mScaled = ctx.measureText(lines[0]);

  // console.log('mscale', mScaled);
  const ascentScaled = mScaled.actualBoundingBoxAscent;
  const descentScaled = mScaled.actualBoundingBoxDescent;
  const heightScaled = ascentScaled + descentScaled;

  {
    /* Uncomment this to view the original text bounding box*/
  }
  // ctx.strokeRect(
  //   x,
  //   y +
  //     ((lineHeightMultiplier - 1) / 2) * baseFontSize +
  //     (baseFontSize -
  //       (baseFontMetrics[0].ascent - baseFontMetrics[0].alphabeticBaseline)) /
  //       2,
  //   baseFontMetrics[0].width,
  //   baseFontMetrics[0].height
  // );

  return { calcFontSize: adjustedFontSize, chosenIndex: chosenIndex };
};

export const measureBaselineOffset = (
  text: string,
  font: string,
  boxX: number,
  boxY: number,
  boxWidth: number,
  boxHeight: number,
  textX: number,
  textY: number
): number => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;

  canvas.width = boxX + boxWidth + 50;
  canvas.height = boxY + boxHeight + 50;

  ctx.font = font;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'black';

  // Measure text metrics
  const metrics = ctx.measureText(text);
  const textDescent = metrics.actualBoundingBoxDescent; // Measure descent
  const boxBottom = boxY + boxHeight;
  const expectedBaselineY = boxBottom - textDescent; // Align text bottom with box bottom

  // Draw box
  ctx.strokeStyle = 'red';
  ctx.lineWidth = 2;
  ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

  // Draw text
  ctx.fillText(text, textX, textY);

  // The offset needed to align text bottom with box bottom
  const offsetY = expectedBaselineY - textY;

  return offsetY;
};

const measureAlphabeticToBottomGap = (text: string, font: string): number => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;

  // Set a large enough canvas size
  canvas.width = 1000;
  canvas.height = 500;

  ctx.font = font;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'black';

  const baselineY = 400; // Use a sufficiently high value to prevent clipping
  ctx.fillText(text, 50, baselineY);

  // Extract image data
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;

  let textBottomY = 0;

  // Scan from bottom to top to find the lowest visible pixel
  for (let y = canvas.height - 1; y >= 0; y--) {
    for (let x = 0; x < canvas.width; x++) {
      const alpha = data[(y * canvas.width + x) * 4 + 3]; // Get alpha channel
      if (alpha > 0) {
        textBottomY = y;
        break;
      }
    }
    if (textBottomY > 0) break;
  }

  const gap = textBottomY - baselineY;
  return gap; // Distance between alphabetic baseline and actual bottom of text
};

const drawText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  fontName: string,
  fontSize: number,
  chosenIndex: number,
  lineHeight: number,
  boundingBoxWidth: number,
  alignment: string,
  x: number,
  y: number,
  flipX: boolean,
  flipY: boolean,
  fontStyle: string,
  fontWeight: string,
  baseFontSize: number,
  fontMetrics: LineMetrics[],
  newFontMetrics: LineMetrics[]
): void => {
  // Save the current context state
  ctx.save();

  // Set font properties
  ctx.font = `${fontStyle} ${fontWeight}  ${fontSize}px '${fontName}'`;
  ctx.textBaseline = 'alphabetic'; // Ensure alignment starts at the aphabetic baseline

  // Split text into lines
  const lines = text.split('\n');

  // Calculate flipping translations based on the bounding box
  const translateX = flipX ? x + boundingBoxWidth / 2 : x;
  const translateY = flipY ? y + (fontSize * lineHeight * lines.length) / 2 : y;

  // Isolate transformations to just the text
  ctx.save();
  ctx.translate(translateX, translateY);
  if (flipX) ctx.scale(-1, 1);
  if (flipY) ctx.scale(1, -1);
  ctx.translate(-translateX, -translateY);

  // Render each line of text

  // First offset is the the line height consideration
  let offsetTop = 0;

  for (const [index, line] of lines.entries()) {
    const lineWidth = ctx.measureText(line).width;

    // Calculate horizontal alignment
    let offsetX = 0;
    if (alignment === 'center') {
      offsetX = (boundingBoxWidth - lineWidth) / 2;
    } else if (alignment === 'right') {
      offsetX = boundingBoxWidth - lineWidth;
    }
    const canvas = document.createElement('canvas');
    const newCtx = canvas.getContext('2d');
    newCtx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontName}`;
    newCtx.textBaseline = 'alphabetic';
    newCtx.fillStyle = 'black';

    // console.log(
    //   'calculated gap',
    //   `${fontStyle} ${fontWeight}  ${fontSize}px '${fontName}'`,
    //   baselineOffset
    // );

    // Draw the text line

    ctx.fillText(line, x + offsetX, y + offsetTop);

    // Update vertical offset for the next line
    offsetTop += fontSize * lineHeight;
  }

  // Restore current text drawing
  ctx.restore();

  // Restore the canvas context state for anything else (e.g., logo)
  ctx.restore();
};

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (error) => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });

export const createCanvasWithOverlay = async (
  originalImage: string,
  overlayData: OverlayData,
  brandData: BrandData,
  replaceColors: boolean,
  canvasRef?: HTMLCanvasElement
): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    try {
      const canvas = canvasRef || document.createElement('canvas');
      const overlayCanvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('Failed to get canvas rendering context');
      await importFont(DEFAULT_EDITOR_FONT_URL);
      await importFont(brandData.fonts.fontFaceUrl);
      await waitForGoogleFonts(brandData.fonts.fontFaceUrl);

      ctx.imageSmoothingEnabled = false;

      // Step 1: Canvas and Base Image Initialization
      const baseImage = await loadImage(originalImage);
      baseImage.crossOrigin = 'anonymous';
      baseImage.src = originalImage;
      await baseImage.decode();

      canvas.width = baseImage.width;
      canvas.height = baseImage.height;
      ctx.drawImage(baseImage, 0, 0, baseImage.width, baseImage.height);

      overlayCanvas.width = baseImage.width;
      overlayCanvas.height = baseImage.height;
      const overlayCtx = overlayCanvas.getContext('2d');

      overlayCtx.clearRect(0, 0, baseImage.width, baseImage.height);

      // Step 2: Color Replacement

      const overlayBlob = await convertSvgToPng(
        overlayData.overlay.url,
        baseImage.width,
        [
          {
            originalColor: overlayData.overlay.primaryColor,
            replacementColor: brandData.primaryColor
          },
          {
            originalColor: overlayData.overlay.secondaryColor,
            replacementColor: brandData.secondaryColor
          }
        ],
        replaceColors
      );

      // Step 3: Overlay Image Loading and Positioning

      const overlayUrl = URL.createObjectURL(overlayBlob);

      // Load the image properly
      const overlayImage = await loadImage(overlayUrl);

      // No need to set crossOrigin since we're using a local blob
      overlayImage.src = overlayUrl;
      await overlayImage.decode();

      const overlayAspectRatio = overlayImage.width / overlayImage.height;
      const baseAspectRatio = baseImage.width / baseImage.height;

      let overlayWidth: number, overlayHeight: number;
      if (overlayAspectRatio > baseAspectRatio) {
        overlayWidth = baseImage.width;
        overlayHeight = overlayWidth / overlayAspectRatio;
      } else {
        overlayHeight = baseImage.height;
        overlayWidth = overlayHeight * overlayAspectRatio;
      }

      const overlayX = calculateAlignmentX(
        overlayData.overlay.align,
        baseImage.width,
        overlayWidth
      );
      const overlayY = calculateAlignmentY(
        overlayData.overlay.align,
        baseImage.height,
        overlayHeight
      );

      overlayCtx.drawImage(
        overlayImage,
        overlayX,
        overlayY,
        overlayWidth,
        overlayHeight
      );

      // Step 4: Adjusting Text and Logos Relative to Overlay

      // Adjust and Draw Text
      for (const textObj of overlayData.text) {
        const {
          text,
          x,
          y,
          width,
          height,
          boxWidth,
          boxHeight,
          color,
          textAlign,
          fontStyle,
          fontWeight,
          baseFontFamily,
          fontSize,
          lineHeight,
          flipX,
          flipY,
          textMetrics
          // plainTextMetrics
        } = textObj;

        const fontName = brandData.fonts.fontFamily;
        const fontUrl = brandData.fonts.fontFaceUrl;
        let checkedFontWeight = fontWeight;
        let checkedFontStyle = fontStyle;
        await checkFontSupportsStyle(fontUrl, {
          weight: fontWeight,
          style: fontStyle
        }).then((result) => {
          checkedFontWeight = result.weight ? fontWeight : 'normal';
          checkedFontStyle = result.style ? fontStyle : 'normal';
        });

        //In here we break the text down into the different lines with a /n so we know what fits into each line
        let newText: string = text;
        if (boxWidth) {
          newText = wrapText(text, boxWidth, fontSize, baseFontFamily);
        }

        ctx.textBaseline = 'alphabetic';
        //The text becomes an array at this point with each line becoming an array object
        const lines = newText.split('\n');
        //we are fetching out original or basefont metrics from the from the the overlaydata to ensure uniformity
        const baseFontMetrics = textMetrics;

        //This is the font metric for the brand. It is the font we are converting to
        const newFontMetrics = await getLineMetrics(
          fontSize,
          lines,
          brandData.fonts.fontFamily,
          checkedFontWeight,
          checkedFontStyle
        );
        // overlayCtx.strokeStyle = 'blue';
        // overlayCtx.lineWidth = 3;
        // overlayCtx.strokeRect(x, y, width, height);

        // overlayCtx.strokeStyle = 'red';
        // overlayCtx.strokeRect(
        //   x,
        //   y + (lineHeight - 1) * fontSize,
        //   width,
        //   height - (lineHeight - 1) * fontSize * 2
        // );

        //Calculating fontSize of the new font that will fit into all the lines
        const { calcFontSize, chosenIndex } = await adjustFontSizes(
          x,
          y,
          overlayCtx,
          newText ? newText : text,
          fontName,
          fontUrl,
          fontSize,
          lineHeight,
          newText ? boxWidth : width,
          height, // Height is not scaled to avoid text distortion
          checkedFontStyle,
          checkedFontWeight,
          baseFontMetrics,
          newFontMetrics
        );

        const newFontSizeMetric = getTextMetrics(
          calcFontSize,
          text,
          fontName,
          checkedFontWeight,
          checkedFontStyle
        );

        overlayCtx.fillStyle = color;

        overlayCtx.font = `${checkedFontStyle} ${checkedFontWeight} ${calcFontSize}px '${fontName}'`;

        // AdjustedY is the calculation of where the bounding box of the baseFont text starts from
        //We make considerations for line height which is 1/2 from the top of the line box
        //We then make consideration for the difference between the full font height and the actual boundingbox height of the text
        //We use alphabeticBaseline as bottom of the text because descent of the text varies often
        //And text are actually place in the center of the ascent position and alphabetic baseline, trying to use the descent as base will shrink the space especially for drooping texts

        const metrics = baseFontMetrics[0];

        const originalBaseline =
          (metrics?.fontAscent / (metrics?.fontAscent + metrics?.fontDescent)) *
          fontSize;
        const baselineYCoordinate =
          y + (lineHeight - 1) * fontSize + originalBaseline;

        // overlayCtx.strokeStyle = 'green';
        // overlayCtx.strokeRect(
        //   x,
        //   y +
        //     (lineHeight - 1) * fontSize +
        //     (originalBaseline - metrics?.ascent),
        //   width,
        //   metrics?.ascent + metrics?.descent
        // );

        // const metrics = newFontMetrics[0];
        // const adjustedY =
        //   y +
        //   (lineHeight - (metrics.ascent + metrics.descent)) / 2 +
        //   (metrics.ascent - metrics.alphabeticBaseline);

        // We draw our text here
        drawText(
          overlayCtx,
          newText ? newText : text,
          fontName,
          calcFontSize,
          chosenIndex,
          lineHeight,
          newText ? boxWidth : width,
          textAlign,
          x,
          baselineYCoordinate,
          flipX,
          flipY,
          checkedFontStyle,
          checkedFontWeight,
          fontSize,
          baseFontMetrics,
          newFontMetrics
        );
      }

      // Adjust and Draw Logos

      for (const logoObj of overlayData.logo) {
        const absoluteX = logoObj.x;
        const absoluteY = logoObj.y;
        const logoWidth = logoObj.width;
        const logoHeight = logoObj.height;

        const BgColor = brandData?.logoBackgroundColor || '#ffffff'; // Default to white if missing

        const sourceImg = brandData.logo?.find(
          (item) => item.type === logoObj.type
        );

        // Ignore if the source image is missing
        if (!sourceImg?.url) continue;

        const logoImage = new Image();
        logoImage.crossOrigin = 'anonymous';
        logoImage.src = sourceImg?.url;

        try {
          await logoImage.decode();
        } catch (error) {
          console.warn(`Failed to load logo: ${sourceImg.url}`, error);
          continue; // Skip this logo if it fails to load
        }
        // Add padding for background
        const padding = 0;
        const borderRadius = 2;
        const availableWidth = logoWidth - padding * 2;
        const availableHeight = logoHeight - padding * 2;

        // Maintain logo aspect ratio
        const aspectRatio = logoImage.width / logoImage.height;
        let drawWidth = availableWidth;
        let drawHeight = drawWidth / aspectRatio;

        if (drawHeight > availableHeight) {
          drawHeight = availableHeight;
          drawWidth = drawHeight * aspectRatio;
        }

        // Adjusted coordinates to account for padding
        const centeredX =
          absoluteX + (logoWidth - (drawWidth + padding * 2)) / 2;
        const centeredY =
          absoluteY + (logoHeight - (drawHeight + padding * 2)) / 2;

        // Draw background with rounded corners
        // overlayCtx.fillStyle = BgColor;
        // overlayCtx.beginPath();
        // overlayCtx.roundRect(
        //  centeredX,
        //  centeredY,
        //  drawWidth + padding * 2,
        //  drawHeight + padding * 2,
        //  borderRadius
        // );
        // overlayCtx.fill();

        // Draw the logo on top of the background
        overlayCtx.drawImage(
          logoImage,
          centeredX + padding, // Shift logo inside background
          centeredY + padding,
          drawWidth,
          drawHeight
        );
      }

      ctx.drawImage(overlayCanvas, 0, 0, baseImage.width, baseImage.height);

      // Step 5: Exporting Image or Returning Canvas
      if (!canvasRef) {
        const getCanvasBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
          return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(blob);
              } else {
                console.error('Failed to create blob from canvas');
                reject(new Error('Failed to create blob from canvas'));
              }
            }, 'image/png');
          });
        };

        const imageBlob = await getCanvasBlob(canvas);

        resolve({ blobUrl: URL.createObjectURL(imageBlob), blob: imageBlob });

        return { blobUrl: URL.createObjectURL(imageBlob), blob: imageBlob };

        // return canvas.toDataURL('image/png');
      }
    } catch (error) {
      console.error('Error in createCanvasWithOverlay:', error);
      reject(error);
      throw error;
    }
  });
};

export const convertSVGtoPNG = async (
  overlayUrl: string,
  imageWidth: number
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas rendering context');

  // Load the raster image
  const overlayImage = new Image();
  overlayImage.crossOrigin = 'anonymous';
  overlayImage.src = overlayUrl;
  await overlayImage.decode();

  // Set canvas size
  const maxImageWidth = imageWidth;
  const maxImageHeight =
    (maxImageWidth * overlayImage.height) / overlayImage.width;

  canvas.width = maxImageWidth;
  canvas.height = maxImageHeight;

  console.log('the dimensions', overlayImage.width, overlayImage.height);

  // Draw image on canvas
  ctx.drawImage(overlayImage, 0, 0, maxImageWidth, maxImageHeight);

  // Convert to Blob
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('Failed to generate Blob from canvas'));
    }, 'image/png'); // PNG is used, but you can change it
  });
};

export const convertOverlayColors = async (
  overlayData: OverlayData,
  brandData: BrandData,
  imageWidth: number,
  replaceColors: boolean
): Promise<Blob> => {
  const overlayBlob = await convertSvgToPng(
    overlayData.overlay.url,
    imageWidth,
    [
      {
        originalColor: overlayData.overlay.primaryColor,
        replacementColor: brandData.primaryColor
      },
      {
        originalColor: overlayData.overlay.secondaryColor,
        replacementColor: brandData.secondaryColor
      }
    ],
    replaceColors
  );

  // Convert to Blob
  return new Promise<Blob>((resolve, reject) => {
    if (overlayBlob) resolve(overlayBlob);
    else reject(new Error('Failed to generate Blob from canvas'));
  });
};

// Function to process PNG/JPG using Canvas

export const convertLogo = async (
  baseImageUrl: string,
  overlayData: OverlayData,
  brandData: BrandData
): Promise<{ blob: Blob; index: number }[]> => {
  try {
    // Load the base image to get its dimensions
    const baseImage = await loadImage(baseImageUrl);
    baseImage.crossOrigin = 'anonymous';
    baseImage.src = baseImageUrl;
    await baseImage.decode();

    // Create a temporary canvas to work with
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas rendering context');

    // Set canvas dimensions to match the base image
    canvas.width = baseImage.width;
    canvas.height = baseImage.height;

    // Load the overlay image to calculate scaling
    const overlayImage = await loadImage(overlayData.overlay.url);
    overlayImage.crossOrigin = 'anonymous';
    overlayImage.src = overlayData.overlay.url;
    await overlayImage.decode();

    // Calculate scaling factors
    const overlayAspectRatio = overlayImage.width / overlayImage.height;
    const baseAspectRatio = baseImage.width / baseImage.height;

    let overlayWidth: number, overlayHeight: number;
    if (overlayAspectRatio > baseAspectRatio) {
      overlayWidth = baseImage.width;
      overlayHeight = overlayWidth / overlayAspectRatio;
    } else {
      overlayHeight = baseImage.height;
      overlayWidth = overlayHeight * overlayAspectRatio;
    }

    const scaleX = overlayWidth / overlayImage.width;
    const scaleY = overlayHeight / overlayImage.height;

    // Calculate overlay position
    const overlayX = calculateAlignmentX(
      overlayData.overlay.align,
      baseImage.width,
      overlayWidth
    );
    const overlayY = calculateAlignmentY(
      overlayData.overlay.align,
      baseImage.height,
      overlayHeight
    );

    // Array to store logo blobs
    const logoBlobs: { blob: Blob; index: number }[] = [];

    // Process each logo
    for (const logoObj of overlayData.logo) {
      const index = overlayData.logo.indexOf(logoObj);

      const sourceImg = brandData.logo?.find(
        (item) => item.type === logoObj.type
      );
      if (!sourceImg) continue;

      const logoImage = new Image();
      logoImage.crossOrigin = 'anonymous';
      logoImage.src = sourceImg.url;
      await logoImage.decode();

      // Calculate scaled position and dimensions for the logo
      const scaledX = overlayX + logoObj.x * scaleX;
      const scaledY = overlayY + logoObj.y * scaleY;
      const scaledWidth = logoObj.width * scaleX;
      const scaledHeight = logoObj.height * scaleY;

      // Maintain logo aspect ratio
      const aspectRatio = logoImage.width / logoImage.height;
      let drawWidth = scaledWidth;
      let drawHeight = drawWidth / aspectRatio;

      if (drawHeight > scaledHeight) {
        drawHeight = scaledHeight;
        drawWidth = drawHeight * aspectRatio;
      }

      // Center the logo within the bounding box
      const centeredX = scaledX + (scaledWidth - drawWidth) / 2;
      const centeredY = scaledY + (scaledHeight - drawHeight) / 2;

      // Create a temporary canvas for the logo
      const tempCanvas = document.createElement('canvas');
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) throw new Error('Failed to get temporary canvas context');

      // Set temporary canvas dimensions to the logo's dimensions
      tempCanvas.width = drawWidth;
      tempCanvas.height = drawHeight;

      // Draw the logo on the temporary canvas
      tempCtx.drawImage(logoImage, 0, 0, drawWidth, drawHeight);

      // Convert the temporary canvas to a blob
      const logoBlob = await new Promise<Blob>((resolve, reject) => {
        tempCanvas.toBlob((blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to create blob from temporary canvas'));
          }
        }, 'image/png');
      });

      logoBlobs.push({ blob: logoBlob, index: index });
    }

    return logoBlobs;
  } catch (error) {
    console.error('Error in adjustLogos:', error);
    throw error;
  }
};

// export const convertLogo = async (
//   originalImage: string,
//   overlayData: OverlayData,
//   brandData: BrandData
// ): Promise<Blob[]> => {
//   const allLogoBlobs: Blob[] = [];

//   // Load the original image to determine its dimensions
//   const baseImage = new Image();
//   baseImage.crossOrigin = 'anonymous';
//   baseImage.src = originalImage;
//   await baseImage.decode();

//   const baseWidth = baseImage.width;
//   const baseHeight = baseImage.height;

//   for (const logoObj of overlayData.logo) {
//     const canvas = document.createElement('canvas');
//     const ctx = canvas.getContext('2d');
//     if (!ctx) {
//       throw new Error('Failed to get canvas rendering context');
//     }

//     // Calculate scaled logo dimensions relative to the original image
//     const scaledX = logoObj.x * baseWidth;
//     const scaledY = logoObj.y * baseHeight;
//     const scaledWidth = logoObj.width * baseWidth;
//     const scaledHeight = logoObj.height * baseHeight;

//     // Set canvas dimensions
//     canvas.width = scaledWidth;
//     canvas.height = scaledHeight;

//     // Draw the background shape (circle or rectangle)
//     if (logoObj.type === 'circle') {
//       const radius = Math.min(scaledWidth, scaledHeight) / 2;
//       ctx.beginPath();
//       ctx.arc(radius, radius, radius, 0, Math.PI * 2);
//       ctx.closePath();
//       ctx.fillStyle = logoObj.bgColor;
//       ctx.fill();
//     } else {
//       ctx.fillStyle = logoObj.bgColor;
//       ctx.fillRect(0, 0, scaledWidth, scaledHeight);
//     }

//     // Load the corresponding logo image
//     const sourceLogo = brandData.logo?.find(
//       (item) => item.type === logoObj.type
//     );
//     if (!sourceLogo) {
//       throw new Error(`No matching logo found for type: ${logoObj.type}`);
//     }

//     const logoImage = new Image();
//     logoImage.crossOrigin = 'anonymous';
//     logoImage.src = sourceLogo.url;
//     await logoImage.decode();

//     // Maintain logo aspect ratio and draw it on the canvas
//     const logoAspectRatio = logoImage.width / logoImage.height;
//     let drawWidth = scaledWidth;
//     let drawHeight = drawWidth / logoAspectRatio;

//     if (drawHeight > scaledHeight) {
//       drawHeight = scaledHeight;
//       drawWidth = drawHeight * logoAspectRatio;
//     }

//     // Center the logo within the bounding box
//     const centerX = (scaledWidth - drawWidth) / 2;
//     const centerY = (scaledHeight - drawHeight) / 2;

//     ctx.drawImage(logoImage, centerX, centerY, drawWidth, drawHeight);

//     // Convert canvas content to a Blob and push it to the array
//     const blob = await new Promise<Blob | null>((resolve) =>
//       canvas.toBlob(resolve, 'image/png')
//     );

//     if (blob) {
//       allLogoBlobs.push(blob);
//     } else {
//       throw new Error('Failed to generate Blob from canvas');
//     }
//   }

//   return allLogoBlobs;
// };

export const convertLogoWithoutBg = async (
  overlayData: OverlayData,
  brandData: BrandData
): Promise<Blob[]> => {
  const allLogoBlobs: Blob[] = [];

  for (const logoObj of overlayData.logo) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas rendering context');
    }

    // Load the logo image
    const logoImage = new Image();
    logoImage.crossOrigin = 'anonymous';
    logoImage.src = logoObj.url;
    await logoImage.decode();

    // Set canvas dimensions to match the logo object
    canvas.width = logoObj.width;
    canvas.height = logoObj.height;

    // Draw the logo image
    ctx.drawImage(logoImage, 0, 0, logoObj.width, logoObj.height);

    // Convert canvas content to a Blob and push it to the array
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/png')
    );

    if (blob) {
      allLogoBlobs.push(blob);
    } else {
      throw new Error('Failed to generate Blob from canvas');
    }
  }

  return allLogoBlobs;
};

export async function calculateEditorFontSize(
  originalImage: string,
  textObj: OverlayData['text'][number],
  brandData: BrandData
): Promise<any> {
  return new Promise(async (resolve, reject) => {
    try {
      importFont(DEFAULT_EDITOR_FONT_URL);
      importFont(brandData.fonts.fontFaceUrl);
      const canvas = document.createElement('canvas');
      const overlayCanvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) throw new Error('Failed to get canvas rendering context');

      // Step 1: Canvas and Base Image Initialization
      const baseImage = await loadImage(originalImage);
      baseImage.crossOrigin = 'anonymous';
      baseImage.src = originalImage;
      await baseImage.decode();

      canvas.width = baseImage.width;
      canvas.height = baseImage.height;
      ctx.drawImage(baseImage, 0, 0, baseImage.width, baseImage.height);

      overlayCanvas.width = baseImage.width;
      overlayCanvas.height = baseImage.height;
      const overlayCtx = overlayCanvas.getContext('2d');

      overlayCtx.clearRect(0, 0, baseImage.width, baseImage.height);

      // Set up the hidden measurement div
      const {
        text,
        x,
        y,
        width,
        height,
        boxWidth,
        boxHeight,
        color,
        textAlign,
        fontStyle,
        fontWeight,
        baseFontFamily,
        fontSize,
        lineHeight,
        flipX,
        flipY,
        textMetrics
      } = textObj;

      const fontName = brandData.fonts.fontFamily;
      const fontUrl = brandData.fonts.fontFaceUrl;
      let checkedFontWeight = fontWeight;
      let checkedFontStyle = fontStyle;
      await checkFontSupportsStyle(fontUrl, {
        weight: fontWeight,
        style: fontStyle
      }).then((result) => {
        console.log('font style supports', result); // { weight: false, style: false }
        checkedFontWeight = result.weight ? fontWeight : 'normal';
        checkedFontStyle = result.style ? fontStyle : 'normal';
      });

      //In here we break the text down into the different lines with a /n so we know what fits into each line
      let newText: string = text;
      if (boxWidth) {
        newText = wrapText(text, boxWidth, fontSize, baseFontFamily);
      }

      ctx.textBaseline = 'alphabetic';
      //The text becomes an array at this point with each line becoming an array object
      const lines = newText.split('\n');
      //we are fetching out original or basefont metrics from the from the the overlaydata to ensure uniformity
      const baseFontMetrics = textMetrics;

      //This is the font metric for the brand. It is the font we are converting to
      const newFontMetrics = await getLineMetrics(
        fontSize,
        lines,
        brandData.fonts.fontFamily,
        checkedFontWeight,
        checkedFontStyle
      );

      //Calculating fontSize of the new font that will fit into all the lines
      const { calcFontSize, chosenIndex } = await adjustFontSizes(
        x,
        y,
        overlayCtx,
        newText ? newText : text,
        fontName,
        fontUrl,
        fontSize,
        lineHeight,
        newText ? boxWidth : width,
        height, // Height is not scaled to avoid text distortion
        checkedFontStyle,
        checkedFontWeight,
        baseFontMetrics,
        newFontMetrics
      );

      const baseFontMetric = await getTextMetrics(
        fontSize,
        lines[chosenIndex],
        DEFAULT_EDITOR_FONT_FAMILY,
        fontWeight,
        checkedFontStyle
      );

      const newFontMetric = await getTextMetrics(
        calcFontSize,
        lines[chosenIndex],
        brandData.fonts.fontFamily,
        checkedFontWeight,
        checkedFontStyle
      );

      const originalBaseline =
        (baseFontMetric?.fontBoundingBoxAscent /
          (baseFontMetric?.fontBoundingBoxAscent +
            baseFontMetric?.fontBoundingBoxDescent)) *
        fontSize;
      const newBaseline =
        (newFontMetric?.fontBoundingBoxAscent /
          (newFontMetric?.fontBoundingBoxAscent +
            newFontMetric?.fontBoundingBoxDescent)) *
        calcFontSize;

      const middleYCoordinate =
        y + ((lineHeight - 1) / 2) * fontSize + fontSize / 2;

      let topDifference = 0;

      if (newBaseline - originalBaseline > 2) {
        topDifference =
          (newBaseline - newFontMetric?.actualBoundingBoxAscent) / 2;
      }
      const lineheightDifference =
        (lineHeight - 1) * fontSize - (lineHeight - 1) * calcFontSize;

      const baselineDifference =
        originalBaseline -
        baseFontMetric?.actualBoundingBoxAscent -
        (newBaseline - newFontMetric?.actualBoundingBoxAscent);

      const baselineYCoordinate =
        y + (lineHeight - 1) * fontSize + originalBaseline;

      const a = baselineYCoordinate;
      const b = calcFontSize - 184.89;

      const newY =
        middleYCoordinate -
        calcFontSize / 2 -
        ((lineHeight - 1) / 2) * calcFontSize;
      // baselineYCoordinate - (lineHeight - 1) * fontSize - calcFontSize;
      const Ydeviation = newY;
      console.log(
        'the values',

        calcFontSize,
        lines[chosenIndex],
        brandData.fonts.fontFamily,
        checkedFontWeight,
        checkedFontStyle,

        originalBaseline / fontSize,
        y,
        calcFontSize,
        newBaseline
      );
      resolve({
        resultFontSize: calcFontSize,
        deviation: Ydeviation
      });

      return { resultFontSize: calcFontSize, deviation: Ydeviation };
    } catch (error) {
      console.error('Error in createCanvasWithOverlay:', error);
      reject(error);
      throw error;
    }
  });
}

async function getTrueAscentDescent(
  text: string,
  font: string,
  fontSize: number
): Promise<{ ascent: number; descent: number }> {
  const canvas = document.createElement('canvas');
  canvas.width = 1000;
  canvas.height = 500;
  const ctx = canvas.getContext('2d')!;
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  // Set baseline in the vertical center for safety
  const baselineY = Math.floor(canvas.height / 2);
  ctx.font = `${fontSize}px '${font}'`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'black';
  ctx.fillText(text, 50, baselineY); // add horizontal margin

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

  let top = -1;
  let bottom = -1;

  // Search for topmost and bottommost visible pixels
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const alpha = imageData[(y * canvas.width + x) * 4 + 3]; // alpha channel
      if (alpha > 0) {
        if (top === -1) top = y;
        bottom = y;
        break;
      }
    }
  }

  const ascent = baselineY - top;
  const descent = bottom - baselineY;

  return { ascent, descent };
}
